import { format, getTime, formatDistanceToNow } from 'date-fns';
import moment from 'moment-timezone';
import dateTimezone from './date-timezone';
import { brandConfig } from '../config';
// ----------------------------------------------------------------------

type InputValue = string | number | Date | null;

/** Format a date in the brand timezone. Use for display where dates should be contextual to the brand (e.g. check-in/check-out). */
export function fDateInBrandTZ(date: InputValue, momentFormat: string = 'MMM D, YYYY'): string {
  if (!date) return '';
  const m = moment(date).tz(brandConfig.timezone);
  return m.isValid() ? m.format(momentFormat) : '';
}

/** Format a date in brand timezone as YYYY-MM-DD for API params (e.g. reservation list start_date/end_date). */
export function formatDateInBrandTZ(date: InputValue, includeTime: boolean = false): string {
  if (!date) return '';
  const m = moment(date).tz(brandConfig.timezone);
  return m.isValid() ? includeTime ? m.format('YYYY-MM-DD HH:mm:ss') : m.format('YYYY-MM-DD') : '';
}

/** Start of the calendar day for the given date in brand timezone, as a Date. */
export function startOfDayInBrandTZ(date: InputValue): Date {
  if (!date) return new Date(0);
  const dayStr = moment(date).tz(brandConfig.timezone).format('YYYY-MM-DD');
  return moment.tz(dayStr, brandConfig.timezone).startOf('day').toDate();
}

/** End of the calendar day for the given date in brand timezone, as a Date. */
export function endOfDayInBrandTZ(date: InputValue): Date {
  if (!date) return new Date(0);
  const dayStr = moment(date).tz(brandConfig.timezone).format('YYYY-MM-DD');
  return moment.tz(dayStr, brandConfig.timezone).endOf('day').toDate();
}

/**
 * Start of the calendar day `daysToAdd` after the given calendar day (`YYYY-MM-DD` in brand timezone).
 * Use with `formatDateInBrandTZ(new Date())` so lead-time windows advance when the brand "today" changes.
 */
export function startOfBrandYmdPlusCalendarDays(ymd: string, daysToAdd: number): Date {
  return moment.tz(ymd, 'YYYY-MM-DD', brandConfig.timezone).startOf('day').add(daysToAdd, 'days').toDate();
}

/**
 * Interpret a date's local calendar day (Y/M/D) as that day in brand timezone.
 * Use for date picker values so "Feb 2" chosen by the user becomes start of Feb 2 in brand TZ.
 */
export function dateFromLocalDayStartInBrandTZ(date: InputValue): Date {
  if (!date) return new Date(0);
  const d = new Date(date);
  return moment
    .tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate() }, brandConfig.timezone)
    .startOf('day')
    .toDate();
}

/** End of that same calendar day in brand timezone. */
export function dateFromLocalDayEndInBrandTZ(date: InputValue): Date {
  if (!date) return new Date(0);
  const d = new Date(date);
  return moment
    .tz({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate() }, brandConfig.timezone)
    .endOf('day')
    .toDate();
}

/**
 * Compact date range in brand timezone: "Mar 14–21, 2026" or "Mar 28 – Apr 4, 2026".
 * Uses en-dash for same-month ranges and spaced en-dash for cross-month.
 */
export function fDateRangeInBrandTZ(start: InputValue, end: InputValue): string {
  if (!start || !end) return '';
  const s = moment(start).tz(brandConfig.timezone);
  const e = moment(end).tz(brandConfig.timezone);
  if (!s.isValid() || !e.isValid()) return '';

  const sameMonth = s.month() === e.month() && s.year() === e.year();
  const sameYear = s.year() === e.year();

  if (sameMonth) {
    return `${s.format('MMM D')}\u2013${e.format('D, YYYY')}`;
  }
  if (sameYear) {
    return `${s.format('MMM D')} \u2013 ${e.format('MMM D, YYYY')}`;
  }
  return `${s.format('MMM D, YYYY')} \u2013 ${e.format('MMM D, YYYY')}`;
}

export function fDate(date: InputValue, newFormat?: string, utc: boolean = false) {
  const fm = newFormat || 'dd MMM yyyy';

  return date
    ? utc
      ? format(new Date(date), fm)
      : format(dateTimezone(date, brandConfig.timezone), fm)
    : '';
}

export function fDateTime(date: InputValue, newFormat?: string, utc: boolean = false) {
  const fm = newFormat || 'dd MMM yyyy p';

  return date
    ? utc
      ? format(new Date(date), fm)
      : format(dateTimezone(date, brandConfig.timezone), fm)
    : '';
}

export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : '';
}

export function fToNow(date: InputValue) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true
      })
    : '';
}

// --------------

export enum FDateFormat {
  localized_us_long_month_day_year = 'PPP', // April 29th, 1453
  localized_us_numeric_month_day_year = 'M/dd/yyyy', // 5/22/2023
  localized_us_short_month_day_year = 'MMM dd, yyyy',
  localized_us_short_month_day = 'MMM dd',
  localized_month_day = 'M/d', // 6/19 10/19
  localized_chat_timestamp = 'MMM d h:mma',
  localized_month_year = 'MMM yyyy',
  localized_year = 'yyyy',
  localized_transfer_format = 'MMM do, yyyy',
  localized_date_time_full = 'M/dd/yyyy, h:mm a',
  localized_month_label = 'LLLL'
}

export type FDateType = keyof typeof FDateFormat;

export const scopedRequestDateFormat = 'yyyy-MM-dd';

export enum WidgetDateModes {
  CurrentMonth = 'CurrentMonth', // July 1 - July 31
  MonthToDate = 'MonthToDate', // July 1 - <today>
  YearToDateExclusive = 'YearToDateExclusive', // January 1 - <end of previous month>
  YearToDate = 'YearToDate', // January 1 - <today>
  YearToDateInclusive = 'YearToDateInclusive', // January 1 - <end of current month>
  PastToCurrentMonth = 'PastToCurrentMonth' // <start of last month> - <end of current month>
}

export const yearChartMonthLabels: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

